import React, { useEffect, useState } from "react";
import { useFarm } from "../services/farmContext";
import { MockAPI } from "../services/api";
import { POINT_KINDS, INACTIVE_POI_COLOR } from "../services/placeModel";
import { useSettingsChanges } from "../services/settingsChanges";
import PlaceIcon from "./PlaceIcon";
import { Button, Card } from "./ui";
export default function PoiCategorySettings() {
  const { activeFarm, refreshActiveFarm } = useFarm();
  const [colors, setColors] = useState<Record<string, string>>({}),
    [baseline, setBaseline] = useState("{}"),
    [farmId, setFarmId] = useState("");
  const [busy, setBusy] = useState(false),
    [message, setMessage] = useState(""),
    [error, setError] = useState("");
  const dirty = JSON.stringify(colors) !== baseline;
  useSettingsChanges(dirty);
  useEffect(() => {
    if (activeFarm && (activeFarm.farm_id !== farmId || !dirty)) {
      const value = activeFarm.poi_colors || {};
      setColors(value);
      setBaseline(JSON.stringify(value));
      setFarmId(activeFarm.farm_id);
    }
  }, [activeFarm, farmId, dirty]);
  const save = async () => {
    if (!activeFarm || activeFarm.farm_id !== farmId) return;
    setBusy(true);
    setMessage("");
    setError("");
    try {
      await MockAPI.saveFarm({ ...activeFarm, poi_colors: colors });
      await refreshActiveFarm();
      setBaseline(JSON.stringify(colors));
      setMessage("Place colors saved.");
    } catch (e) {
      setError(
        e instanceof Error ? e.message : "Could not save colors. Try again.",
      );
    } finally {
      setBusy(false);
    }
  };
  return (
    <section className="space-y-5">
      <div>
        <h2 className="text-xl font-semibold">Place colors</h2>
        <p className="text-sm text-gray-500 mt-1">
          Choose one color for each place type at{" "}
          {activeFarm?.name || "your farm"}. Everyone sees these colors on the
          map.
        </p>
      </div>
      <Card className="p-5 sm:p-6 space-y-5">
        <div className="grid sm:grid-cols-2 gap-3">
          {POINT_KINDS.map((kind) => (
            <label
              key={kind.value}
              className="flex items-center gap-3 rounded-xl border p-3"
            >
              <PlaceIcon
                place={{ point_kind: kind.value }}
                color={colors[kind.value] || kind.color}
              />
              <span className="text-sm flex-1">{kind.label}</span>
              <input
                type="color"
                disabled={busy}
                aria-label={`${kind.label} color`}
                value={colors[kind.value] || kind.color}
                onChange={(e) => {
                  setColors((c) => ({ ...c, [kind.value]: e.target.value }));
                  setMessage("");
                }}
                className="h-10 w-12 border rounded cursor-pointer"
              />
            </label>
          ))}
        </div>
        <div className="rounded-lg bg-gray-50 p-4 flex items-start gap-3 text-sm">
          <span
            className="h-6 w-6 rounded-md border border-white shadow shrink-0"
            style={{ background: INACTIVE_POI_COLOR }}
          />
          <div>
            <p className="font-medium">Inactive places</p>
            <p className="text-gray-500 mt-1">
              All inactive points use this gray. Each person can show or hide
              them in their map preferences.
            </p>
          </div>
        </div>
        {error && (
          <p role="alert" className="text-sm text-red-700">
            {error}
          </p>
        )}
        <div className="flex flex-wrap justify-between items-center gap-3 border-t pt-4">
          <Button
            variant="outline"
            disabled={busy}
            onClick={() => {
              setColors({});
              setMessage("");
            }}
          >
            Restore default colors
          </Button>
          <Button disabled={!activeFarm || !dirty || busy} onClick={save}>
            {busy ? "Saving…" : "Save colors"}
          </Button>
        </div>
        {message && (
          <p role="status" className="text-sm text-usda-700">
            {message}
          </p>
        )}
      </Card>
    </section>
  );
}
